/**
 * The contract this feature expects from the backend. Nothing implements it
 * yet, menu_core_be has no shift entity, so this file doubles as the spec to
 * hand over.
 *
 * Shape follows the app's existing conventions: times as minutes from midnight
 * (no timezone ambiguity for wall-clock rostering), ids as UUIDs, and the week
 * addressed by its Monday so a GET and a PUT talk about the same unit.
 */

/** A shift as the backend returns it, `id` is always present. */
export interface ShiftDto {
  id: string
  userId: string
  /** ISO calendar day, YYYY-MM-DD, a bare date, never a timestamp */
  date: string
  /** Minutes from midnight, 0-1440 */
  startMinutes: number
  /** Minutes from midnight, 0-1440; always greater than startMinutes */
  endMinutes: number
  /**
   * One of the values in `types/shiftType.model.ts`.
   *
   * Optional on the wire so a server that predates the field keeps working: an
   * absent value reads as `WORK`, which is what every shift stored before this
   * existed is. Once the column lands the backend should always send it.
   *
   * All-day types (`TIME_OFF`, `VACATION`, `SICK_LEAVE`, `UNAVAILABLE`) travel
   * as 0-1440 rather than as null times, so the shape is the same for every
   * entry and no consumer needs a special case.
   */
  type?: string
  /** Omitted when empty, never null, so the dirty check stays stable */
  note?: string
}

/** A shift as the client sends it, no `id` yet when it is being created. */
export type UpsertShiftDto = Omit<ShiftDto, 'id'> & { id?: string }

/** Colour assigned to an employee in the schedule, hex. */
export interface EmployeeShiftColorDto {
  userId: string
  color: string
}

/**
 * One week of the roster, addressed by its Monday.
 *
 * PUT replaces the whole week: shifts missing from the payload are deleted.
 * That mirrors the floor plan's whole-document write and keeps a week of
 * drag-and-drop edits to a single request instead of dozens of chatty ones.
 *
 * Colours are the exception, they are NOT scoped to a week, so an absent
 * `colors` means "leave them alone", never "delete them".
 */
export interface ScheduleWeekDto {
  /** Monday of the week, YYYY-MM-DD */
  weekStart: string
  shifts: ShiftDto[]
  colors?: EmployeeShiftColorDto[]
}

export interface UpsertScheduleWeekDto
  extends Omit<ScheduleWeekDto, 'shifts'> {
  shifts: UpsertShiftDto[]
}

export enum SCHEDULE_QUERY {
  WEEK = 'scheduleWeek'
}
